import React, { Component, lazy } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import "./styles/transitions.css";
import Layout from "./containers/Layout";
import Loader from "./views/loader/Loader";
import { Toaster } from "react-hot-toast";
import EditProgram from "./views/Admin/EditProgram";
import AddProgram from "./views/Admin/AddProgram";
import { Provider } from "react-redux";
import { store } from "./store/store";

// Session Management
import SessionList from "./views/Admin/Sessions/SessionList";
import AddSession from "./views/Admin/Sessions/AddSession";
import EditSession from "./views/Admin/Sessions/EditSession";

// Cart Management
const Cart = lazy(() => import("./views/Cart/Cart"));

//order management
const OrderConfirmation = lazy(() => import("./views/Order/OrderConfirmation"));

// Lazy loaded components
const Hero = lazy(() => import("./views/Main/Hero"));
const ContactUs = lazy(() => import("./views/Main/ContactUs"));
const Pricing = lazy(() => import("./views/Main/Pricing"));
const Subscription = lazy(() => import("./views/Main/Subscription"));
const SubscriptionCheckout = lazy(() =>
  import("./views/Main/SubscriptionCheckout")
);
const SubscriptionConfirmation = lazy(() =>
  import("./views/Main/SubscriptionConfirmation")
);
const Profile = lazy(() => import("./views/profile/Profile"));
const Login = lazy(() => import("./views/login/Login"));
const Register = lazy(() => import("./views/login/Register"));
const ForgotPassword = lazy(() => import("./views/login/ForgotPassword"));
const CustomerUpdate = lazy(() => import("./views/Main/CustomerUpdate"));
const Sessions = lazy(() => import("./views/Main/Sessions"));
const SubscribedSessions = lazy(() => import("./views/Main/SubscribedSessions"));
const SubscribedTutorials = lazy(() => import("./views/Main/SubscribedTutorials"));
const MyOrders = lazy(() => import("./views/Order/MyOrders"));

// Admin Components
const AdminLayout = lazy(() => import("./views/Admin/AdminLayout"));
const Dashboard = lazy(() => import("./views/Admin/Dashboard"));
const Trainers = lazy(() => import("./views/Admin/Trainers"));
const AddTrainer = lazy(() => import("./views/Admin/AddTrainer"));
const EditTrainer = lazy(() => import("./views/Admin/EditTrainer"));
const Programs = lazy(() => import("./views/Admin/Programs"));
const Orders = lazy(() => import("./views/Admin/Orders"));
const Customers = lazy(() => import("./views/Admin/Customers"));
const CustomerSubscription = lazy(() => import("./views/Admin/CustomerSubscription"));
const TutorialList = lazy(() => import("./views/Admin/Tutorials/TutorialList"));
const AddTutorial = lazy(() => import("./views/Admin/Tutorials/AddTutorial"));
const EditTutorial = lazy(() => import("./views/Admin/Tutorials/EditTutorial"));
const TutorialLogsReport = lazy(() => import("./views/Admin/Tutorials/TutorialLogsReport"));
const ManageTutorialDocuments = lazy(() => import("./views/Admin/Tutorials/ManageTutorialDocuments"));
const TipsList = lazy(() => import("./views/Admin/Tips/TipsList"));
const AddTips = lazy(() => import("./views/Admin/Tips/AddTips"));
const EditTips = lazy(() => import("./views/Admin/Tips/EditTips"));
const PregnancyJourneyList = lazy(() => import("./views/Admin/PregnancyJourney/PregnancyJourneyList"));
const AddPregnancyJourney = lazy(() => import("./views/Admin/PregnancyJourney/AddPregnancyJourney"));
const EditPregnancyJourney = lazy(() => import("./views/Admin/PregnancyJourney/EditPregnancyJourney"));

// Banner Management
const BannerList = lazy(() => import("./views/Admin/Banners/BannerList"));
const AddBanner = lazy(() => import("./views/Admin/Banners/AddBanner"));
const EditBanner = lazy(() => import("./views/Admin/Banners/EditBanner"));
const CustomerBannerDetails = lazy(() =>
  import("./views/Banner/BannerDetails")
);

// Routes component with CSS transitions
const AppRoutes = () => {
  const location = useLocation();

  return (
    <div className="page-transition">
      <Routes location={location} key={location.pathname}>
        <Route element={<Layout />}>
          <Route path="/" name="Main" element={<Hero />} />
          <Route
            path="/customer/profile"
            name="Profile"
            element={<Profile />}
          />
          <Route path="/contact" name="Contact Us" element={<ContactUs />} />
          <Route path="/pricing" name="Pricing" element={<Pricing />} />
          <Route
            path="/subscription"
            name="Subscription"
            element={<Subscription />}
          />
          <Route
            path="/subscription-checkout"
            name="Subscription Checkout"
            element={<SubscriptionCheckout />}
          />
          <Route
            path="/subscription/confirmation/:subscriptionId"
            name="Subscription Confirmation"
            element={<SubscriptionConfirmation />}
          />
          <Route path="/cart" element={<Cart />} />
          <Route path="/banner/:bannerId" element={<CustomerBannerDetails />} />
          <Route
            path="/order/confirmation/:orderId"
            element={<OrderConfirmation />}
          />
          <Route path="/subscribedSessions" name="subscribedSessions" element={<SubscribedSessions />} />
          <Route path="/subscribedTutorials" name="subscribedTutorials" element={<SubscribedTutorials />} />
          <Route path="/sessions" name="Sessions" element={<Sessions />} />
          <Route
            path="/customer/update"
            name="Customer Update"
            element={<CustomerUpdate />}
          />
          <Route path="/myorders" name="My Orders" element={<MyOrders />} />
        </Route>
        <Route path="/login" name="Login" element={<Login />} />
        <Route path="/register" name="Register" element={<Register />} />
        <Route path="/forgotPassword" name="Forgot Password" element={<ForgotPassword />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="trainers" element={<Trainers />} />
          <Route path="trainers/add" element={<AddTrainer />} />
          <Route path="trainers/edit/:trainerId" element={<EditTrainer />} />
          <Route path="programs" element={<Programs />} />
          <Route path="programs/add" element={<AddProgram />} />
          <Route path="programs/edit/:programId" element={<EditProgram />} />

          <Route path="tutorials" element={<TutorialList />} />
          <Route path="tutorials/add" element={<AddTutorial />} />
          <Route path="tutorials/edit/:tutorialId" element={<EditTutorial />} />
          <Route path="tutorials/:tutorialId/documents" element={<ManageTutorialDocuments />} />
          <Route path="report/tutorial-logs" element={<TutorialLogsReport />} />

          {/* Session Management Routes */}
          <Route path="sessions" element={<SessionList />} />
          <Route path="sessions/add" element={<AddSession />} />
          <Route path="sessions/edit/:sessionId" element={<EditSession />} />

          {/* Banner Management Routes */}
          <Route path="banners" element={<BannerList />} />
          <Route path="banners/add" element={<AddBanner />} />
          <Route path="banners/edit/:bannerId" element={<EditBanner />} />

          <Route path="tips" element={<TipsList />} />
          <Route path="tips/add" element={<AddTips />} />
          <Route path="tips/edit/:tipsId" element={<EditTips />} />

          <Route path="pregnancy-journey" element={<PregnancyJourneyList />} />
          <Route path="pregnancy-journey/add" element={<AddPregnancyJourney />} />
          <Route path="pregnancy-journey/edit/:pregnancyJourneyWeekId" element={<EditPregnancyJourney />} />

          <Route path="orders" element={<Orders />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customer/subscription" element={<CustomerSubscription />} />
        </Route>
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <div>
          <React.Suspense fallback={<Loader />}>
            <Toaster position="bottom-center" />
            <AppRoutes />
          </React.Suspense>
        </div>
      </BrowserRouter>
    </Provider>
  );
}

export default App;
